import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import type { AuditLog, LogFilters, Paginated } from '@/types/auditLog';
import { AuditLogDetailDialog } from './AuditLogDetailDialog';
import { Pager } from './Pager';

interface Props {
  filters: LogFilters;
  onFiltersChange: (f: LogFilters) => void;
  refreshTrigger: number;
}

export function AuditLogTable({ filters, onFiltersChange, refreshTrigger }: Props) {
  const [result, setResult] = useState<Paginated<AuditLog> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);

  useEffect(() => {
    api.auditLogs
      .list(filters)
      .then((r) => {
        setResult(r);
        setError(null);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load audit logs'));
  }, [filters, refreshTrigger]);

  return (
    <>
      {error && <div className="mb-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
      {!result ? (
        <div className="py-8 text-center text-muted-foreground">Loading audit logs…</div>
      ) : result.items.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground">No audit entries match these filters.</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Resource</TableHead>
              <TableHead>Result</TableHead>
              <TableHead>IP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.items.map((log) => (
              <TableRow key={log.id} className="cursor-pointer" onClick={() => setSelected(log.id)}>
                <TableCell className="text-sm whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</TableCell>
                <TableCell className="text-sm">
                  {log.actor_type === 'api_key' ? log.api_key_name || `#${log.api_key_id}` : <Badge variant="outline">{log.actor_type}</Badge>}
                </TableCell>
                <TableCell className="font-mono text-xs">{log.action}</TableCell>
                <TableCell className="text-sm">
                  {log.resource_type}
                  {log.resource_id !== null && ` #${log.resource_id}`}
                </TableCell>
                <TableCell>
                  <Badge variant={log.success ? 'default' : 'destructive'}>{log.status_code}</Badge>
                  {log.error_code && <span className="ml-2 text-xs text-muted-foreground">{log.error_code}</span>}
                </TableCell>
                <TableCell className="text-sm font-mono">{log.ip_address}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {result && (
        <Pager page={result.page} totalPages={result.total_pages} total={result.total} onPage={(page) => onFiltersChange({ ...filters, page })} />
      )}
      <AuditLogDetailDialog id={selected} onClose={() => setSelected(null)} />
    </>
  );
}
